import { Box, Card, Typography, Divider, Stack } from "@mui/material";
import { Circle } from "@mui/icons-material";
import { RegionData } from "../types";
import { formatCurrency } from "../../../shared/utils";

interface GeographicViewProps {
  data: RegionData[];
}

const getRagColor = (
  rag: "green" | "amber" | "red"
): "success" | "warning" | "error" => {
  switch (rag) {
    case "green":
      return "success";
    case "amber":
      return "warning";
    case "red":
      return "error";
  }
};

export const GeographicView = ({ data }: GeographicViewProps) => {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Typography variant="subtitle2">Network Areas View</Typography>
        <Typography variant="caption" color="text.secondary">
          Regional performance and delivery
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: { xs: "nowrap", sm: "wrap", lg: "nowrap" },
            gap: 2,
          }}
        >
          {data.map((region) => (
            <Box
              key={region.region}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(33.333% - 16px)",
                  lg: "1 1 0",
                },
                minWidth: 0,
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: "100%",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    {region.region}
                  </Typography>
                  <Circle
                    fontSize="small"
                    color={getRagColor(region.rag)}
                    sx={{ fontSize: 16 }}
                  />
                </Box>

                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Budget Spend
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="baseline"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="h5" fontWeight={600}>
                        {region.budgetSpend}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(Number(region.budgetSpendValue))} /{" "}
                        {formatCurrency(Number(region.approvedBudget))}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Outputs Delivered
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="baseline"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="h5" fontWeight={600}>
                        {region.outputsDelivered}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {region.outputsDeliveredValue}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Total Spend
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="baseline"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {formatCurrency(Number(region.totalSpendValue))} /{" "}
                        {formatCurrency(Number(region.totalPlannedBudget))}
                      </Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {region.totalSpendValue}
                      </Typography> */}
                    </Box>
                  </Box>
                </Stack>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
};
